import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import HasPermission from "src/core/guards/has-permission";
import * as yup from "yup";
import ModalComponent from "../../../core/components/modal/modal.component";
import TableComponent from "../../../core/components/table/table.component";
import SkeletonComponent from "../../../shared/custom-spinner/skeleton.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../shared/form-builder/form-builder.component";
import { getAllFaultType } from "../../../store/slices/fault-module/fault-report.slice";
import {
  acknowledgementFault,
  resolveFault,
  setFilter,
} from "../../../store/slices/fault.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";
import * as moment from "moment";

interface FaultTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  filter: any;
  onRefetchData: (
    tcCode?: any,
    siteCode?: any,
    blockCode?: any,
    isAcknowledged?: any
  ) => void;
  isLoading: boolean;
}

const FaultTableComponent: FC<FaultTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  filter,
  onRefetchData,
  isLoading = false,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAcknowledgeOpen, setIsAcknowledgeOpen] = useState<boolean>(false);
  const [isAcknowledgeDisabled, setIsAcknowledgeDisabled] =
    useState<boolean>(true);
  const [isResolveDisabled, setIsResolveDisabled] = useState<boolean>(true);
  const [isResolveOpen, setIsResolveOpen] = useState<boolean>(false);
  const [isFilterInitiated, setIsFilterInitiated] = useState<boolean>(false);
  const [showMixedStatusWarning, setShowMixedStatusWarning] =
    useState<boolean>(false);

  const thisMonth = moment().format("YYYY-MM");
  const fiveMonthsAgo = moment().subtract(5, "months").format("YYYY-MM");

  const [modalLabel, setModalLabel] = useState<any>("");
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [allTypesFault, setAllTypesFault] = useState<any>([
    {
      label: "All Fault Categories",
      value: 0,
    },
  ]);
  const acknowledgeStatusOptions = [
    { label: "Acknowledged", value: 1 },
    { label: "Reported", value: 2 },
    { label: "Resolved", value: 4 },
    { label: "All Status", value: 3 },
  ];
  const [initialValues, setInitialValues] = useState<any>({
    /*     address: "",
    sensorId: "",
    faultDateTime: "",
    gateway: "",
    faultDescription: "", */
    remarks: "",
  });

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: filter?.townCouncil || "",
    site: filter?.site || "",
    block: filter?.block || "",
    isAcknowledged: filter?.isAcknowledged || "3",
    faultType: filter?.faultType || 0,
    startDate: filter?.startDate || thisMonth,
  });

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const dispatch = useDispatch();

  const faultDetails = useSelector((state: any) => {
    return state.fault.faultDetails;
  });

  const isLoadingFaultDetails = useSelector((state: any) => {
    return state.fault.isLoadingFaultDetails;
  });

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, []);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const selectedItems = useSelector((state: any) => state.table.selectedItems);

  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  const clearSearchParameters = () => {
    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState({}, document.title, url.toString());
  };

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    if (formFilterInitialValues.townCouncil.length) {
      setFilterUpdateCount(filterUpdateCount + 1);
    }
  }, [formFilterInitialValues]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);

    if (filter.townCouncil) {
      dispatch(getSiteByTCCode({ tcCode: filter.townCouncil }));
    }
    if (filter.townCouncil && filter.site) {
      dispatch(getBlocksBySiteCode({ siteCode: filter.site }));
    }
  }, []);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
    }));

    setIsFilterInitiated(true);

    if (!selectedTC) return;

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
    }));
    setIsFilterInitiated(true);

    if (!selectedSite) return;

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (block: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: block,
    }));
  };
  const onChangeAcknowledged = (isAcknowledged: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      isAcknowledged: isAcknowledged,
    }));
  };
  const onChangeFaultType = (faultType: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      faultType: faultType,
    }));
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));
  };

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    dispatch(setFilter(form.values));

    // clear toggle select all and select
    dispatch({
      type: "table/toggleSelectAll",
      payload: [],
    });

    dispatch({
      type: "table/toggleSelect",
      payload: { page: null, value: null, status: null },
    });

    // Clear URL search parameters
    clearSearchParameters();
    setIsFilterInitiated(true);
  };

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      isAcknowledged: "3",
      faultType: 0,
      startDate: thisMonth,
    });

    dispatch(setFilter({}));

    // clear toggle select all and select
    dispatch({
      type: "table/toggleSelectAll",
      payload: [],
    });

    dispatch({
      type: "table/toggleSelect",
      payload: { page: null, value: null, status: null },
    });

    // Clear URL search parameters
    clearSearchParameters();
    setIsFilterInitiated(true);

    setSiteOptions([]);
    setBlockOptions([]);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsAcknowledgeOpen(false);
    setIsResolveOpen(false);

    setInitialValues({
      address: "",
      sensorId: "",
      faultDateTime: "",
      gateway: "",
      faultDescription: "",
      remarks: "",
    });
  };

  const acknowledgeButtonAction = () => {
    // Check if mixed statuses and show warning
    if (showMixedStatusWarning) {
      toast.warning("Mixed selection detected. Please select only 'Reported' items to acknowledge", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setModalLabel("Fault Acknowledge");
    setIsAcknowledgeOpen(true);
    setIsModalOpen(true);

    // setSelectedCodeToAcknowledge(id);
    // dispatch(getFaultById({ id }));
  };

  const resolveButtonAction = () => {
    setModalLabel("Fault Resolve");
    setIsResolveOpen(true);
    setIsModalOpen(true);

    // setSelectedCodeToResolve(id);
    // dispatch(getFaultById({ id }));
  };

  useEffect(() => {
    dispatch(getAllFaultType()).then((res: any) => {
      const types = res?.payload?.data?.map((type: any) => ({
        label: type?.faultName,
        value: type?.id,
      }));
      setAllTypesFault(types);
    });
  }, []);

  useEffect(() => {
    if (!faultDetails || !Object.keys(faultDetails).length) return;
    setInitialValues((prevValues: any) => ({
      ...prevValues,
      address: faultDetails?.address || "",
      sensorId: faultDetails?.assetCode || "",
      faultDateTime: faultDetails?.timestamp || "",
      gateway: faultDetails?.gateway || "",
      faultDescription: faultDetails?.faultDescription || "",
      remarks: faultDetails?.remarks || "",
    }));
  }, [faultDetails]);

  const onSubmitModal = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const { remarks } = form.values;

      if (remarks?.length > 200) return;

      const finalItems = selectedItems?.filter(
        (item: any) => item?.value != null
      );

      if (isAcknowledgeOpen) {
        const payload = {
          alarmReadingIds: finalItems?.map((item: any) => item?.value),
          remarks: remarks,
        };

        dispatch(acknowledgementFault({ payload })).then(() => {
          dispatch({
            type: "table/toggleSelectAll",
            payload: [],
          });

          dispatch({
            type: "table/toggleSelect",
            payload: { page: null, value: null, status: null },
          });

          handleCloseModal();
          onRefetchData();
        });
      } else if (isResolveOpen) {
        const payload = {
          alarmReadingIds: finalItems?.map((item: any) => item?.value),
          remarks: remarks,
        };

        dispatch(resolveFault({ payload })).then(() => {
          dispatch({
            type: "table/toggleSelectAll",
            payload: [],
          });

          dispatch({
            type: "table/toggleSelect",
            payload: { page: null, value: null, status: null },
          });

          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  const modalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitModal,
      variant: "primary",
    },
  ];

  const filterActionConfig: any = {
    label: "Filter",
    event: onSubmitFilter,
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const emptyConfig = {
    title: "No Fault Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "remarks",
        label: "Remarks",
        placeholder: "Remarks",
        type: "textarea",
      },
    ],
  };

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "isAcknowledged",
            placeholder: "Select Status",
            type: "select",
            options: acknowledgeStatusOptions,
            onChange: onChangeAcknowledged,
            required: false,
            isLoading: false,
            hideSelectPlaceholder: true,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "faultType",
            placeholder: "Select Fault Type",
            type: "select",
            options: allTypesFault,
            onChange: onChangeFaultType,
            required: true,
            isLoading: false,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "startDate",
            placeholder: "Select Month",
            type: "month",
            onChange: onChangeStartDate,
            required: true,
            isLoading: false,
            minDate: fiveMonthsAgo,
            maxDate: thisMonth,
          },
        ],
      },
    ],
  };

  const validationSchema = yup.object().shape({
    /* blockNumber: yup.string().required("Block Id is required"),
    sensorId: yup.string().required("Sensor Id is required"),
    faultDateTime: yup.string().required("Report Date & Time is required"),
    gateway: yup.string().required("Gateway is required"),
    zone: yup.string().required("Zone is required"), */
    remarks: yup
      .string()
      .required("Remarks is required")
      .max(200, "Remarks must be at most 200 characters"),
  });

  useEffect(() => {
    if (!isFilterInitiated) {
      if (
        townCouncilData?.data?.length > 0 ||
        siteDataByTCCode?.data?.length > 0 ||
        blockDataSiteCode?.data?.length > 0
      ) {
        setFormFilterInitialValues({
          ...formFilterInitialValues,
          townCouncil: filter?.townCouncil || "",
          site: filter?.site || "",
          block: filter?.block || "",
          isAcknowledged: filter?.isAcknowledged || "3",
          faultType: filter?.faultType || 0,
          startDate: filter?.startDate || thisMonth,
        });
      }
    }
  }, [filter, tcOptions, siteOptions, blockOptions, allTypesFault]); // These are required dependencies, dont remove

  useEffect(() => {
    // Check if all items have the same status
    const getUniformStatus = (items: any[]) => {
      if (!items || items.length === 0) return false;

      const firstStatus = items[0]?.status;
      const allSameStatus = items.every(
        (item: any) => item?.status === firstStatus
      );

      return allSameStatus ? firstStatus : false;
    };

    // Remove first item from selectedItems before passing to function
    const itemsWithoutFirst = selectedItems?.slice(1) || [];
    const uniformStatus = getUniformStatus(itemsWithoutFirst);

    // Set warning state for mixed statuses
    setShowMixedStatusWarning(
      uniformStatus === false && itemsWithoutFirst.length > 0
    );

    if (selectedItems?.length > 1) {
      if (uniformStatus === "Acknowledged") {
        // Only Resolve button enabled, Acknowledge button disabled
        setIsResolveDisabled(false);
        setIsAcknowledgeDisabled(true);
      } else if (uniformStatus === "Reported") {
        // Only Acknowledge button enabled, Resolve button disabled
        setIsResolveDisabled(true);
        setIsAcknowledgeDisabled(false);
      } else if (uniformStatus === false) {
        // Only Acknowledge button enabled, Resolve button disabled
        setIsResolveDisabled(true);
        setIsAcknowledgeDisabled(false);
      } else {
        // Default behavior for other statuses
        setIsResolveDisabled(true);
        setIsAcknowledgeDisabled(true);
      }
    } else {
      setIsResolveDisabled(true);
      setIsAcknowledgeDisabled(true);
    }
  }, [selectedItems]);

  return (
    <div>
      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          filterUpdateCount={filterUpdateCount}
        />
      </div>

      <div style={{ marginTop: "5rem" }}>
        <div className="my-3 mt-3">
          <div className="d-flex justify-content-end table-generic-action">
            <HasPermission
              moduleName="Fault"
              actionName="Acknowledge-Fault"
              isRoute={false}
            >
              <div className="d-flex justify-end items-end">
                <B2Button
                  variant="primary"
                  onClick={acknowledgeButtonAction}
                  disabled={isAcknowledgeDisabled}
                >
                  Acknowledge
                </B2Button>
                <B2Button
                  variant="primary"
                  onClick={resolveButtonAction}
                  className="ms-2"
                  disabled={isResolveDisabled}
                >
                  Resolve
                </B2Button>
              </div>
            </HasPermission>
          </div>
          <div className="mt-3">
            <TableComponent
              tableHeaders={columns}
              data={data}
              totalNumberOfRecords={totalNumberOfRecords}
              pageSize={pageSize}
              current={current}
              onChangePage={onChangePage}
              // actionConfig={actionConfig}
              emptyConfig={emptyConfig}
              onChangeSort={onChangeSort}
              isLoadingData={isLoading}
              enableSelection={true}
              isFaultTable={true}
            />
          </div>
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={modalLabel}
          actionConfig={modalActionConfig}
        >
          <SkeletonComponent
            wrapperHeight="380px"
            skeletonHeight="50px"
            isLoading={isLoadingFaultDetails}
            count={6}
          >
            <FormBuilderComponent
              formRef={formRef}
              initialValues={initialValues}
              validation={validationSchema}
              formConfig={formConfig}
            />
          </SkeletonComponent>
        </ModalComponent>
      )}
    </div>
  );
};

export default FaultTableComponent;
